import React from 'react';
import { motion } from 'framer-motion';
import { XIcon } from 'lucide-react';
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 40
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: 'easeOut'
    }
  }
};
export function SalesContent() {
  return <div className="w-full" data-id="element-149">
      {/* Section A: The Pain */}
      <section className="py-20 bg-brand-cream px-4" data-id="element-150">
        <div className="max-w-3xl mx-auto" data-id="element-151">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeInUp} className="prose prose-lg md:prose-xl prose-p:text-gray-700 prose-p:leading-relaxed mx-auto" data-id="element-152">
            <h2 className="text-3xl md:text-4xl text-brand-navy font-bold italic mb-8 text-center" data-id="element-153">
              Llegas a casa después de un día agotador…
            </h2>
            <p data-id="element-154">
              Llegas a casa después de un día agotador. Intentas entablar una
              conversación normal y, de la nada, un portazo hace vibrar las
              paredes. Te quedas solo en el pasillo, con el corazón acelerado,
              sintiendo que has perdido el control de tu propia casa y que tu
              hijo se ha convertido en un extraño que te mira con desprecio.
            </p>
            <p className="font-medium text-brand-navy" data-id="element-155">
              Si sientes que caminas sobre cáscaras de huevo cada vez que hablas
              con tu hijo, no es que seas un mal padre. Es que las herramientas
              de disciplina que funcionaron contigo están obsoletas para el
              cerebro adolescente de hoy.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Section B: The Problem */}
      <section className="py-20 bg-brand-cream-light px-4" data-id="element-156">
        <div className="max-w-3xl mx-auto" data-id="element-157">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeInUp} data-id="element-158">
            <h2 className="text-3xl md:text-4xl text-brand-navy font-bold mb-8 text-center" data-id="element-159">
              El problema (sin vueltas)
            </h2>
            <div className="bg-white p-8 md:p-10 rounded-2xl shadow-soft border border-brand-cream" data-id="element-160">
              <p className="text-lg text-gray-700 mb-6 leading-relaxed" data-id="element-161">
                Entras en una habitación y el ambiente se tensa. Pides que
                ordene su mochila y recibes un "sos un pesado" o, peor aún, un
                silencio gélido. Intentas razonar, intentas explicar, pero
                terminas gritando. Al final, el sentimiento es siempre el mismo:
                culpa, frustración y la sensación de que el vínculo se rompe un
                poco más cada día.
              </p>
              <div className="bg-brand-orange/10 border-l-4 border-brand-orange p-6 rounded-r-lg" data-id="element-162">
                <p className="text-xl font-serif text-brand-navy font-semibold m-0" data-id="element-163">
                  El problema no es la falta de amor, es la falta de un canal de
                  comunicación compatible con un cerebro que está en plena
                  reconfiguración biológica.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Section C: Why past attempts failed */}
      <section className="py-20 bg-brand-cream px-4" data-id="element-164">
        <div className="max-w-4xl mx-auto" data-id="element-165">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeInUp} data-id="element-166">
            <h2 className="text-3xl md:text-4xl text-brand-navy font-bold mb-12 text-center" data-id="element-167">
              Por qué lo que ya intentaste no funciona
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-168">
              {['El autoritarismo de “se hace porque lo digo yo” hoy solo genera una rebeldía más violenta y secreta.', 'Intentar razonar lógicamente cuando tu hijo está en medio de un estallido es imposible porque su parte lógica está “desconectada”.', 'La permisividad total para evitar el conflicto te quita el respeto y deja al adolescente sin el marco de seguridad que necesita.', 'Los sermones largos solo logran que tu hijo aprenda a “desconectar” el oído apenas empiezas a hablar.'].map((text, i) => <motion.div key={i} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: i * 0.1,
              duration: 0.5
            }} className="flex items-start gap-4 bg-white p-6 rounded-xl shadow-sm border border-gray-100" data-id="element-169">
                  <div className="flex-shrink-0 mt-1 bg-red-100 p-1.5 rounded-full" data-id="element-170">
                    <XIcon className="w-5 h-5 text-red-500" data-id="element-171" />
                  </div>
                  <p className="text-gray-700 leading-relaxed" data-id="element-172">{text}</p>
                </motion.div>)}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Section D: The Solution Introduction */}
      <section className="py-24 bg-brand-navy text-white px-4 relative overflow-hidden" data-id="element-173">
        <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-brand-orange via-transparent to-transparent" data-id="element-174" />
        <div className="max-w-3xl mx-auto relative z-10 text-center" data-id="element-175">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeInUp} data-id="element-176">
            <h2 className="text-3xl md:text-5xl font-bold mb-8" data-id="element-177">
              Presentamos el{' '}
              <span className="text-brand-orange" data-id="element-178">Cortafuegos Verbal</span>
            </h2>
            <p className="text-xl md:text-2xl font-serif italic text-brand-cream mb-8" data-id="element-179">
              Este no es un libro de psicología teórica: es un manual operativo
              de intervención inmediata.
            </p>
            <p className="text-lg text-gray-300 leading-relaxed" data-id="element-180">
              El Cortafuegos Verbal es un sistema de micro-scripts validados que
              interrumpen la escalada emocional antes de que se convierta en
              incendio. Aprendes a hablar directamente a la parte del cerebro de
              tu hijo que todavía puede escucharte, manteniendo tu autoridad
              intacta y el canal de comunicación abierto.
            </p>
          </motion.div>
        </div>
      </section>
    </div>;
}